import React, { useState, useEffect } from 'react';
import { getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember } from '../../services/team-members-api';
import Button from '../common/Button';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import './TeamMembers.css';

export default function TeamMembers({ teamId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (teamId) {
      loadMembers();
    }
  }, [teamId]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading team members for team:', teamId);
      const response = await getTeamMembers(teamId);
      console.log('Received team members response:', response);
      setMembers(response.members || []);
    } catch (err) {
      console.error('Error loading team members:', err);
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      console.log('Submitting team member:', { teamId, formData, editing: !!editingMember });
      
      if (editingMember) {
        const response = await updateTeamMember(teamId, editingMember.playerId, formData);
        console.log('Update response:', response);
      } else {
        const response = await createTeamMember(teamId, formData);
        console.log('Create response:', response);
      }
      
      console.log('Reloading members...');
      await loadMembers();
      handleCancel();
    } catch (err) {
      console.error('Error saving team member:', err);
      setError(err.message || 'Failed to save team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name} from the team?`)) {
      return;
    }

    try {
      setError(null);
      await deleteTeamMember(teamId, member.playerId);
      await loadMembers();
    } catch (err) {
      setError(err.message || 'Failed to delete team member');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMember(null);
    setFormData({ name: '', email: '', phone: '' });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="team-members">
      <div className="team-members-header">
        <h2>Team Members</h2>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            + Add Member
          </Button>
        )}
      </div>

      {error && <ErrorMessage message={error} onRetry={loadMembers} />}

      {showForm && (
        <div className="member-form-card">
          <h3>{editingMember ? 'Edit Member' : 'Add New Member'}</h3>
          <form onSubmit={handleSubmit} className="member-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={handleCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : (editingMember ? 'Update' : 'Add Member')}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="members-list">
        {members.length === 0 ? (
          <div className="empty-state">
            <p>No team members yet. Add your first member to get started!</p>
          </div>
        ) : (
          <div className="members-grid">
            {members.map((member) => (
              <div key={member.playerId} className="member-card">
                <div className="member-info">
                  <h4>{member.name}</h4>
                  <p className="member-email">{member.email}</p>
                  {member.phone && <p className="member-phone">{member.phone}</p>}
                </div>
                <div className="member-actions">
                  <button
                    onClick={() => handleEdit(member)}
                    className="action-btn edit-btn"
                    title="Edit member"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(member)}
                    className="action-btn delete-btn"
                    title="Remove member"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
